import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  getFurnitureProject,
  getFurnitureProjectByShareToken,
  getFurnitureProjectByViewToken,
  getFurnitureImageUrl,
  updateFurnitureRoom,
} from '@/lib/api';
import { Button } from '@/components/ui/button';
import { ArrowLeft, ImageOff, X, Maximize2, Minimize2, ImageIcon, Map, Printer, Eye, EyeOff } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { printImagePages } from '@/lib/printImages';

/**
 * Project-level mood board overview.
 *
 * Pulls the project doc, picks each room's first mood board (lowest
 * `position`, fallback `created_at`) and arranges them as a responsive
 * grid. Clicking a tile opens a lightbox with the full-size image. Rooms
 * with no mood boards render a muted placeholder so the layout stays
 * uniform and you can spot gaps at a glance.
 */
export default function ProjectViewPage() {
  const { projectId, shareToken, viewToken } = useParams();
  const navigate = useNavigate();

  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null); // { url, name, roomName }
  const [expanded, setExpanded] = useState(false);
  // Toggle to surface rooms the admin has hidden from the grid. Hidden
  // tiles render dimmed with an Unhide action when this is on; they're
  // completely excluded when it's off (default).
  const [showHidden, setShowHidden] = useState(false);
  // 'moodboards' (default) or 'floorplans' — controls which room-level
  // collection is summarised as the project tile grid.
  const [kind, setKind] = useState('moodboards');

  // Share / view tokens are read-only public links — no hide controls.
  const canEdit = !shareToken && !viewToken;

  const load = useCallback(async () => {
    try {
      let data;
      if (shareToken) data = await getFurnitureProjectByShareToken(shareToken);
      else if (viewToken) data = await getFurnitureProjectByViewToken(viewToken);
      else data = await getFurnitureProject(projectId);
      setProject(data);
    } catch { toast.error('Failed to load project'); }
    setLoading(false);
  }, [projectId, shareToken, viewToken]);

  useEffect(() => { load(); }, [load]);

  const goBack = () => {
    if (shareToken) navigate(`/furniture-projects/share/${shareToken}`);
    else if (viewToken) navigate(`/furniture-projects/view/${viewToken}`);
    else if (typeof window !== 'undefined' && window.location.pathname.startsWith('/staff/')) {
      navigate(`/staff/projects/${projectId}`);
    }
    else navigate(`/furniture-projects/${projectId}`);
  };

  const openRoomMb = (roomId) => {
    const suffix = kind === 'floorplans' ? 'floor-plans' : 'mood-board';
    if (shareToken) navigate(`/furniture-projects/share/${shareToken}/rooms/${roomId}/${suffix}`);
    else if (viewToken) navigate(`/furniture-projects/view/${viewToken}/rooms/${roomId}/${suffix}`);
    else if (typeof window !== 'undefined' && window.location.pathname.startsWith('/staff/')) {
      navigate(`/staff/projects/${projectId}/rooms/${roomId}/${suffix}`);
    }
    else navigate(`/furniture-projects/${projectId}/rooms/${roomId}/${suffix}`);
  };

  if (loading) return (
    <div className="min-h-screen bg-[#fbfaf7] flex items-center justify-center">
      <div className="animate-pulse text-[#5b6773]">Loading…</div>
    </div>
  );
  if (!project) return (
    <div className="min-h-screen bg-[#fbfaf7] flex items-center justify-center">
      <p className="text-[#5b6773]">Project not found</p>
    </div>
  );

  const rooms = project.rooms || [];
  const sourceKey = kind === 'floorplans' ? 'floor_plans' : 'mood_boards';
  const labelPlural = kind === 'floorplans' ? 'floor plan' : 'mood board';
  const allTiles = rooms.map(r => {
    const items = (r[sourceKey] || [])
      .slice()
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0) || String(a.created_at).localeCompare(String(b.created_at)));
    return {
      id: r.id, name: r.name, roomType: r.room_type,
      hidden: !!r.hidden_from_view,
      top: items[0] || null,
    };
  });
  // For public/share viewers, hidden rooms are always invisible — no
  // toggle to bring them back. Admins can flip `showHidden` to see them.
  const hiddenCount = allTiles.filter(t => t.hidden).length;
  const tiles = canEdit && showHidden
    ? allTiles
    : allTiles.filter(t => !t.hidden);

  // Toggle a room's hidden_from_view. Optimistically update the in-memory
  // project so the grid responds immediately; reload on failure.
  const toggleRoomHidden = async (roomId, nextHidden) => {
    const prev = project;
    setProject(p => ({
      ...p,
      rooms: (p.rooms || []).map(r => r.id === roomId ? { ...r, hidden_from_view: nextHidden } : r),
    }));
    try {
      await updateFurnitureRoom(projectId, roomId, { hidden_from_view: nextHidden });
      toast.success(nextHidden ? 'Room hidden' : 'Room unhidden');
    } catch {
      setProject(prev);
      toast.error('Failed to update room visibility');
    }
  };

  // ── Print helpers ──────────────────────────────────────────────
  // Flatten every (room, board) into a sorted list of pages for the
  // print window. `kindKey` is 'mood_boards' or 'floor_plans'. Hidden
  // rooms are skipped so the printed deck mirrors what the user sees.
  const collectPages = (kindKey) => {
    const pages = [];
    rooms.forEach((r) => {
      if (r.hidden_from_view) return;
      const items = (r[kindKey] || [])
        .slice()
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0) || String(a.created_at).localeCompare(String(b.created_at)));
      items.forEach((b) => {
        pages.push({
          url: getFurnitureImageUrl(b.url),
          caption: r.name,
          subcaption: b.name || (kindKey === 'floor_plans' ? 'Floor plan' : 'Mood board'),
        });
      });
    });
    return pages;
  };

  const runPrint = (kindKey) => {
    const pages = kindKey === 'all'
      ? [...collectPages('mood_boards'), ...collectPages('floor_plans')]
      : collectPages(kindKey);
    if (pages.length === 0) {
      toast.error(`No ${kindKey === 'floor_plans' ? 'floor plans' : kindKey === 'mood_boards' ? 'mood boards' : 'images'} to print yet.`);
      return;
    }
    printImagePages(
      pages,
      `${project.name} — ${kindKey === 'floor_plans' ? 'Floor Plans' : kindKey === 'mood_boards' ? 'Mood Boards' : 'Mood Boards + Floor Plans'}`,
    );
  };

  // Single-board print used by the per-tile printer icon.
  const printSingleBoard = (board, roomName) => {
    if (!board) return;
    printImagePages([{
      url: getFurnitureImageUrl(board.url),
      caption: roomName,
      subcaption: board.name || (kind === 'floorplans' ? 'Floor plan' : 'Mood board'),
    }], `${roomName} — ${board.name || (kind === 'floorplans' ? 'Floor plan' : 'Mood board')}`);
  };

  const mbCount = rooms.filter(r => !r.hidden_from_view).reduce((s, r) => s + ((r.mood_boards || []).length), 0);
  const fpCount = rooms.filter(r => !r.hidden_from_view).reduce((s, r) => s + ((r.floor_plans || []).length), 0);

  return (
    <div className="min-h-screen bg-[#fbfaf7]">
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-[#e7e2d4]">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={goBack} className="h-8 w-8" data-testid="pv-back">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="min-w-0 flex-1">
            <h1 className="text-lg sm:text-xl font-heading font-semibold text-[#1f2933] truncate" data-testid="pv-title">
              {project.name}
            </h1>
            <p className="text-xs text-[#5b6773]">
              {tiles.length} room{tiles.length === 1 ? '' : 's'} · {tiles.filter(t => t.top).length} with {labelPlural}
              {canEdit && hiddenCount > 0 && !showHidden && (
                <span className="ml-1 text-[#a8324a]">· {hiddenCount} hidden</span>
              )}
            </p>
          </div>

          {/* Kind toggle: Mood Boards ↔ Floor Plans */}
          <div className="inline-flex items-center bg-[#f1ede4] rounded-full p-0.5 text-xs" data-testid="pv-kind-tabs">
            <button
              type="button"
              onClick={() => setKind('moodboards')}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full transition-colors ${kind === 'moodboards' ? 'bg-white shadow text-[#1f2933]' : 'text-[#5b6773] hover:text-[#1f2933]'}`}
              data-testid="pv-tab-moodboards"
            >
              <ImageIcon className="h-3 w-3" /> Mood Boards
            </button>
            <button
              type="button"
              onClick={() => setKind('floorplans')}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full transition-colors ${kind === 'floorplans' ? 'bg-white shadow text-[#1f2933]' : 'text-[#5b6773] hover:text-[#1f2933]'}`}
              data-testid="pv-tab-floorplans"
            >
              <Map className="h-3 w-3" /> Floor Plans
            </button>
          </div>
          {rooms.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 gap-1.5 text-xs"
                  data-testid="pv-print-menu-btn"
                  title="Print or save as PDF"
                >
                  <Printer className="h-3.5 w-3.5" /> Print
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>Print all</DropdownMenuLabel>
                <DropdownMenuItem
                  onClick={() => runPrint('mood_boards')}
                  disabled={mbCount === 0}
                  data-testid="pv-print-moodboards"
                >
                  <ImageIcon className="h-3.5 w-3.5 mr-2" />
                  Mood boards
                  <span className="ml-auto text-xs text-muted-foreground">{mbCount}</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => runPrint('floor_plans')}
                  disabled={fpCount === 0}
                  data-testid="pv-print-floorplans"
                >
                  <Map className="h-3.5 w-3.5 mr-2" />
                  Floor plans
                  <span className="ml-auto text-xs text-muted-foreground">{fpCount}</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => runPrint('all')}
                  disabled={mbCount + fpCount === 0}
                  data-testid="pv-print-all"
                >
                  <Printer className="h-3.5 w-3.5 mr-2" />
                  Everything
                  <span className="ml-auto text-xs text-muted-foreground">{mbCount + fpCount}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {canEdit && hiddenCount > 0 && (
            <Button
              variant={showHidden ? 'default' : 'outline'}
              size="sm"
              className="h-8 gap-1.5 text-xs"
              onClick={() => setShowHidden(v => !v)}
              data-testid="pv-show-hidden-toggle"
              title={showHidden ? 'Hide hidden rooms again' : `Reveal ${hiddenCount} hidden room${hiddenCount === 1 ? '' : 's'}`}
            >
              {showHidden ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
              {showHidden ? `Showing hidden (${hiddenCount})` : `Show hidden (${hiddenCount})`}
            </Button>
          )}
          {rooms.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              className="h-8 gap-1.5 text-xs"
              onClick={() => setExpanded((v) => !v)}
              data-testid="pv-toggle-expand"
              title={expanded ? 'Back to uniform grid' : 'Show mood boards at full width'}
            >
              {expanded ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
              {expanded ? 'Collapse' : 'Expand'}
            </Button>
          )}
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
        {rooms.length === 0 ? (
          <p className="text-sm text-[#5b6773] italic">No rooms yet.</p>
        ) : expanded ? (
          // Expanded layout: uniform-height row with width following each
          // mood board's natural aspect ratio. Rooms wrap to the next row
          // when they overflow the container.
          <div
            className="flex flex-wrap gap-4 sm:gap-6 items-start"
            data-testid="pv-grid"
          >
            {tiles.map(t => (
              <RoomTile
                key={t.id}
                tile={t}
                expanded
                onZoom={() => t.top && setLightbox({
                  url: t.top.url,
                  name: t.top.name,
                  roomName: t.name,
                  roomId: t.id,
                })}
                onOpenRoom={() => openRoomMb(t.id)}
                onPrint={t.top ? () => printSingleBoard(t.top, t.name) : null}
                onToggleHidden={canEdit ? () => toggleRoomHidden(t.id, !t.hidden) : null}
              />
            ))}
          </div>
        ) : (
          <div
            className="grid gap-4 sm:gap-6"
            style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}
            data-testid="pv-grid"
          >
            {tiles.map(t => (
              <RoomTile
                key={t.id}
                tile={t}
                onZoom={() => t.top && setLightbox({
                  url: t.top.url,
                  name: t.top.name,
                  roomName: t.name,
                  roomId: t.id,
                })}
                onOpenRoom={() => openRoomMb(t.id)}
                onPrint={t.top ? () => printSingleBoard(t.top, t.name) : null}
                onToggleHidden={canEdit ? () => toggleRoomHidden(t.id, !t.hidden) : null}
              />
            ))}
          </div>
        )}
      </main>

      {lightbox && (
        <Lightbox
          url={lightbox.url}
          name={lightbox.name}
          roomName={lightbox.roomName}
          onClose={() => setLightbox(null)}
          onOpenRoom={() => { const id = lightbox.roomId; setLightbox(null); openRoomMb(id); }}
        />
      )}
    </div>
  );
}

function RoomTile({ tile, onZoom, onOpenRoom, onPrint, onToggleHidden, expanded = false }) {
  const hasMb = !!tile.top;
  const isHidden = !!tile.hidden;

  // Expanded: render the image at a uniform height with width set by its
  // own aspect ratio. We let the browser shrink-to-fit by setting `width:
  // auto` on the image and dropping the fixed `aspect-[4/3]` cell.
  // Tiles flex-wrap in the parent container, so wide composites take more
  // horizontal space and rooms drop to the next row when they overflow.
  if (expanded) {
    const TILE_H = 320; // px — uniform row height
    return (
      <div
        className={`group rounded-xl overflow-hidden bg-white border border-[#e7e2d4] shadow-[0_1px_0_rgba(16,24,40,0.04)] hover:shadow-[0_8px_28px_rgba(16,24,40,0.10)] transition-shadow flex flex-col max-w-full ${isHidden ? 'opacity-60' : ''}`}
        data-testid={`pv-tile-${tile.id}`}
      >
        <button
          type="button"
          onClick={hasMb ? onZoom : onOpenRoom}
          className="bg-[#f1ede4] relative overflow-hidden flex items-center justify-center max-w-full"
          aria-label={hasMb ? `View ${tile.name} mood board enlarged` : `Open ${tile.name} mood boards`}
          data-testid={`pv-tile-img-${tile.id}`}
          style={{ height: TILE_H }}
        >
          {hasMb ? (
            <img
              src={getFurnitureImageUrl(tile.top.url)}
              alt={tile.top.name || tile.name}
              className="h-full w-auto max-w-full object-contain transition-transform duration-500 group-hover:scale-[1.02]"
              loading="lazy"
              draggable={false}
            />
          ) : (
            <div
              className="flex flex-col items-center justify-center text-[#5b6773]/50 gap-1 px-12"
              style={{ width: Math.round(TILE_H * 4 / 3) }}
            >
              <ImageOff className="h-7 w-7" />
              <p className="text-[11px] uppercase tracking-wider">No mood board</p>
            </div>
          )}
          {isHidden && (
            <span className="absolute top-2 left-2 text-[10px] uppercase tracking-wider bg-[#1f2933] text-white px-2 py-0.5 rounded-full inline-flex items-center gap-1">
              <EyeOff className="h-2.5 w-2.5" /> Hidden
            </span>
          )}
        </button>
        <div className="px-3 py-2.5 flex items-center justify-between gap-2">
          <div className="min-w-0">
            <p className="text-sm font-medium text-[#1f2933] truncate">{tile.name}</p>
            {tile.roomType && tile.roomType !== tile.name && (
              <p className="text-[11px] text-[#5b6773] truncate">{tile.roomType}</p>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {onToggleHidden && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onToggleHidden(); }}
                className={`p-1 rounded transition-colors ${isHidden ? 'text-emerald-700 hover:bg-emerald-100' : 'text-[#5b6773] hover:text-[#1f2933] hover:bg-[#f1ede4]'}`}
                title={isHidden ? 'Unhide this room' : 'Hide this room from the project view'}
                data-testid={`pv-tile-hide-${tile.id}`}
              >
                {isHidden ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
              </button>
            )}
            {hasMb && onPrint && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onPrint(); }}
                className="p-1 rounded text-[#5b6773] hover:text-[#1f2933] hover:bg-[#f1ede4] transition-colors"
                title="Print this board"
                data-testid={`pv-tile-print-${tile.id}`}
              >
                <Printer className="h-3.5 w-3.5" />
              </button>
            )}
            <button
              type="button"
              onClick={onOpenRoom}
              className="text-[11px] text-[#a8324a] hover:underline whitespace-nowrap"
              data-testid={`pv-tile-open-${tile.id}`}
            >
              View room →
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`group rounded-xl overflow-hidden bg-white border border-[#e7e2d4] shadow-[0_1px_0_rgba(16,24,40,0.04)] hover:shadow-[0_8px_28px_rgba(16,24,40,0.10)] transition-shadow ${isHidden ? 'opacity-60' : ''}`} data-testid={`pv-tile-${tile.id}`}>
      <button
        type="button"
        onClick={hasMb ? onZoom : onOpenRoom}
        className="block w-full aspect-[4/3] bg-[#f1ede4] relative overflow-hidden"
        aria-label={hasMb ? `View ${tile.name} mood board enlarged` : `Open ${tile.name} mood boards`}
        data-testid={`pv-tile-img-${tile.id}`}
      >
        {hasMb ? (
          <img
            src={getFurnitureImageUrl(tile.top.url)}
            alt={tile.top.name || tile.name}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
            draggable={false}
          />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-[#5b6773]/50 gap-1">
            <ImageOff className="h-7 w-7" />
            <p className="text-[11px] uppercase tracking-wider">No mood board</p>
          </div>
        )}
        {isHidden && (
          <span className="absolute top-2 left-2 text-[10px] uppercase tracking-wider bg-[#1f2933] text-white px-2 py-0.5 rounded-full inline-flex items-center gap-1">
            <EyeOff className="h-2.5 w-2.5" /> Hidden
          </span>
        )}
      </button>
      <div className="px-3 py-2.5 flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-medium text-[#1f2933] truncate">{tile.name}</p>
          {tile.roomType && tile.roomType !== tile.name && (
            <p className="text-[11px] text-[#5b6773] truncate">{tile.roomType}</p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {onToggleHidden && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onToggleHidden(); }}
              className={`p-1 rounded transition-colors ${isHidden ? 'text-emerald-700 hover:bg-emerald-100' : 'text-[#5b6773] hover:text-[#1f2933] hover:bg-[#f1ede4]'}`}
              title={isHidden ? 'Unhide this room' : 'Hide this room from the project view'}
              data-testid={`pv-tile-hide-${tile.id}`}
            >
              {isHidden ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
            </button>
          )}
          {hasMb && onPrint && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onPrint(); }}
              className="p-1 rounded text-[#5b6773] hover:text-[#1f2933] hover:bg-[#f1ede4] transition-colors"
              title="Print this board"
              data-testid={`pv-tile-print-${tile.id}`}
            >
              <Printer className="h-3.5 w-3.5" />
            </button>
          )}
          <button
            type="button"
            onClick={onOpenRoom}
            className="text-[11px] text-[#a8324a] hover:underline whitespace-nowrap"
            data-testid={`pv-tile-open-${tile.id}`}
          >
            View room →
          </button>
        </div>
      </div>
    </div>
  );
}

function Lightbox({ url, name, roomName, onClose, onOpenRoom }) {
  // Close on Escape — saves the user a click when scanning many boards.
  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/85 flex flex-col"
      onClick={onClose}
      data-testid="pv-lightbox"
    >
      <div className="flex items-center justify-between px-4 sm:px-6 py-3 text-white" onClick={(e) => e.stopPropagation()}>
        <div className="min-w-0">
          <p className="text-sm font-semibold truncate">{roomName}</p>
          {name && <p className="text-xs text-white/60 truncate">{name}</p>}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onOpenRoom}
            className="text-xs px-3 py-1.5 rounded border border-white/30 hover:border-white/60 transition-colors"
            data-testid="pv-lightbox-open-room"
          >
            View all in room
          </button>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-2 rounded hover:bg-white/10 transition-colors"
            data-testid="pv-lightbox-close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-4 sm:p-8 overflow-hidden">
        <img
          src={getFurnitureImageUrl(url)}
          alt={name || roomName}
          className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
          onClick={(e) => e.stopPropagation()}
          draggable={false}
        />
      </div>
    </div>
  );
}
